import { z } from 'zod'

/**
 * Übergabeprotokoll — the handover record for a property.
 *
 * Three variants share one engine. They differ only in which template they are
 * seeded from (which meters, which keys, which rooms, whether defects and the
 * deposit are relevant), never in code path:
 *
 *   rental_movein   Vermietung — Einzug
 *   rental_moveout  Vermietung — Auszug
 *   sale_handover   Übergabe eines Verkaufsobjekts
 *
 * A completed protocol is a legal record of the condition of a property at a
 * point in time. That is why `body` is versioned as a whole and frozen on
 * completion rather than being edited in place — see `handovers/store.ts`.
 */

export const handoverKindSchema = z.enum(['rental_movein', 'rental_moveout', 'sale_handover'])
export type HandoverKind = z.infer<typeof handoverKindSchema>

/**
 * `amending`: a completed protocol that is being changed. The last frozen
 * version stays the valid document until the change is finalised into the next
 * one — see *Fassungen* below.
 */
export const handoverStatusSchema = z.enum(['draft', 'completed', 'amending'])
export type HandoverStatus = z.infer<typeof handoverStatusSchema>

export const HANDOVER_STATUS_LABELS: Record<HandoverStatus, string> = {
  draft: 'Entwurf',
  completed: 'abgeschlossen',
  amending: 'in Änderung',
}

export const HANDOVER_KIND_LABELS: Record<HandoverKind, string> = {
  rental_movein: 'Vermietung — Einzug',
  rental_moveout: 'Vermietung — Auszug',
  sale_handover: 'Übergabe Verkaufsobjekt',
}

/** Roles are fixed so the PDF can lay out signature blocks deterministically. */
export const partyRoleSchema = z.enum(['handover', 'receiver', 'agent', 'witness'])
export type PartyRole = z.infer<typeof partyRoleSchema>

/**
 * Role wording depends on the kind: the person handing over is a Vermieter on a
 * rental and a Verkäufer on a sale. Kept here so the API, the UI and the PDF
 * cannot disagree about what to call someone.
 */
export const PARTY_ROLE_LABELS: Record<HandoverKind, Record<PartyRole, string>> = {
  rental_movein: {
    handover: 'Vermieter / Übergeber',
    receiver: 'Mieter / Übernehmer',
    agent: 'Makler',
    witness: 'Zeuge',
  },
  rental_moveout: {
    handover: 'Mieter / Übergeber',
    receiver: 'Vermieter / Übernehmer',
    agent: 'Makler',
    witness: 'Zeuge',
  },
  sale_handover: {
    handover: 'Verkäufer / Übergeber',
    receiver: 'Käufer / Übernehmer',
    agent: 'Makler',
    witness: 'Zeuge',
  },
}

/**
 * What a party's address field means, which is not the same on every kind.
 *
 * At an Auszug the Mieter is LEAVING, so the useful address is the one they are
 * moving to — that is where the Kautionsabrechnung and the
 * Nebenkostenabrechnung have to be sent. Recording the flat they just vacated
 * would be recording the address at the top of the page a second time.
 */
export function partyAddressLabel(kind: HandoverKind, role: PartyRole): string {
  return kind === 'rental_moveout' && role === 'handover' ? 'Neue Adresse des Mieters' : 'Anschrift'
}

/**
 * Whether a party's address may be filled from onOffice.
 *
 * Not at an Auszug, for the tenant who is leaving: the field asks where they
 * are moving TO, and the address the CRM holds is — with luck — the flat being
 * handed back. Filling it in put the old address under a label that says
 * "Neue Adresse", in the one field the Kautionsabrechnung is sent to, and a
 * field that already has something in it is a field nobody asks about.
 *
 * Derived from the label rather than repeating its condition, so the two
 * cannot drift apart.
 */
export function partyAddressIsPrefilled(kind: HandoverKind, role: PartyRole): boolean {
  return partyAddressLabel(kind, role) === 'Anschrift'
}

/**
 * Bank details are asked for exactly once: from the tenant handing the flat
 * back, because the deposit has to be paid to somebody. Nobody else's account
 * belongs in a handover protocol.
 */
export function partyNeedsBankDetails(kind: HandoverKind, role: PartyRole): boolean {
  return kind === 'rental_moveout' && role === 'handover'
}

export const partySchema = z.object({
  id: z.string(),
  role: partyRoleSchema,
  name: z.string().default(''),
  address: z.string().default(''),
  phone: z.string().default(''),
  email: z.string().default(''),
  /**
   * IBAN / Kontoinhaber of the departing tenant. Defaulted so protocols written
   * before this existed still parse; only collected where it is relevant.
   */
  bankDetails: z.string().default(''),
  /** Present at the appointment. An absent party still gets a signature line. */
  present: z.boolean().default(true),
  /**
   * The onOffice address this party was filled from, or null when it was typed
   * in. An ID and nothing more — it is what lets the editor tell "already in
   * the protocol" from "still on offer" without comparing names, which breaks
   * the moment somebody corrects a spelling. Defaulted, so protocols written
   * before it existed still parse.
   */
  sourceAddressId: z.number().int().nullable().default(null),
})
export type Party = z.infer<typeof partySchema>

/**
 * Which onOffice Kontaktart marks a tenant as FORMER, and which as current.
 *
 * onOffice keeps no dates on the link between a person and an object, so "who
 * lives there now" is not something the API can answer. What it does have is
 * the agency's own tagging, and that is what this goes by: across the 137
 * rental objects with more than one linked renter, 135 had every renter tagged
 * with one of these, and "Mieter, not Vormieter" left exactly one person on 101
 * of them. Neither the order onOffice returns renters in (the Vormieter came
 * first on only 66%) nor `Eintragsdatum` (when the CONTACT was created — one
 * current tenant has been in the CRM since 2018) is good enough to drop anybody.
 *
 * Matched against the LABEL, case-insensitively, never the key: "Vormieter" is
 * a custom value whose key is `indMulti1782Select5900` in this installation and
 * something else in the next.
 *
 * The tag is on the PERSON, not on the link, so one contact can carry both —
 * former tenant of one flat, current tenant of another. "Former" wins.
 */
export const FORMER_TENANT_CONTACT_TYPES = ['Vormieter'] as const
export const CURRENT_TENANT_CONTACT_TYPES = ['Mieter'] as const

/** `unknown`: linked as a renter, tagged as neither. Treated as current. */
export const tenancySchema = z.enum(['current', 'former', 'unknown'])
export type Tenancy = z.infer<typeof tenancySchema>

/** What the editor prints next to a name. `unknown` prints nothing. */
export const TENANCY_LABELS: Record<Tenancy, string | null> = {
  current: 'Mieter',
  former: 'Vormieter',
  unknown: null,
}

/**
 * Somebody onOffice links to the estate, offered to the editor as a party.
 *
 * FETCHED LIVE, NEVER STORED. Only a candidate that is actually taken becomes
 * part of the protocol; the others — the former tenants of a flat, with their
 * telephone numbers — stay in the CRM where they already were.
 */
export const partyCandidateSchema = z.object({
  addressId: z.number().int(),
  /** The role this person would take in THIS protocol — it depends on the kind. */
  role: z.enum(['handover', 'receiver']),
  name: z.string(),
  address: z.string(),
  phone: z.string(),
  email: z.string(),
  /** Set for renters only. An owner or a buyer has no tenancy. */
  tenancy: tenancySchema.nullable(),
  /**
   * What the prefill takes by itself. False does not mean "wrong" — at an
   * Auszug with both a Mieter and a Vormieter linked NOBODY is suggested,
   * because the tag cannot say which of them is handing the flat back.
   */
  suggested: z.boolean(),
})
export type PartyCandidate = z.infer<typeof partyCandidateSchema>

export const handoverContactsSchema = z.object({
  /**
   * False when onOffice could not be read. The editor has to be able to tell
   * "nobody is linked" from "could not ask", or it tells an agent standing in a
   * stairwell with no signal that the flat has no tenant.
   */
  available: z.boolean(),
  candidates: z.array(partyCandidateSchema),
})
export type HandoverContacts = z.infer<typeof handoverContactsSchema>

export const meterKindSchema = z.enum([
  'electricity',
  'electricity_night',
  'gas',
  'water_cold',
  'water_hot',
  'heat',
  'other',
])
export type MeterKind = z.infer<typeof meterKindSchema>

export const METER_UNITS: Record<MeterKind, string> = {
  electricity: 'kWh',
  electricity_night: 'kWh',
  gas: 'm³',
  water_cold: 'm³',
  water_hot: 'm³',
  heat: 'MWh',
  other: '',
}

/**
 * What a meter kind is called — the placeholder in the editor and the name in
 * the change history when a meter has no label of its own.
 */
export const METER_KIND_LABELS: Record<MeterKind, string> = {
  electricity: 'Strom',
  electricity_night: 'Strom (Nachttarif)',
  gas: 'Gas',
  water_cold: 'Wasser (kalt)',
  water_hot: 'Wasser (warm)',
  heat: 'Wärmemenge',
  other: 'Sonstiger Zähler',
}

export const meterReadingSchema = z.object({
  id: z.string(),
  kind: meterKindSchema,
  /** Free label, so "Strom (Keller)" and "Strom (Garage)" can coexist. */
  label: z.string().default(''),
  /** Zählernummer. */
  serial: z.string().default(''),
  /** Kept as a string: German decimals, leading zeros and "abgelesen" notes. */
  value: z.string().default(''),
  unit: z.string().default(''),
  /**
   * Where the reading came from. `ocr` is not implemented — the field exists so
   * that reading a meter photo automatically can be added later without a
   * migration, and so a machine-read value is always distinguishable from one a
   * person typed and checked.
   */
  source: z.enum(['manual', 'ocr']).default('manual'),
  note: z.string().default(''),
  photoIds: z.array(z.string()).default([]),
})
export type MeterReading = z.infer<typeof meterReadingSchema>

export const keyItemSchema = z.object({
  id: z.string(),
  label: z.string().default(''),
  count: z.number().int().min(0).default(0),
  note: z.string().default(''),
})
export type KeyItem = z.infer<typeof keyItemSchema>

export const roomConditionSchema = z.enum(['ok', 'defects', 'not_inspected'])
export type RoomCondition = z.infer<typeof roomConditionSchema>

export const ROOM_CONDITION_LABELS: Record<RoomCondition, string> = {
  ok: 'ohne Beanstandung',
  defects: 'Mängel',
  not_inspected: 'nicht begangen',
}

export const defectSchema = z.object({
  id: z.string(),
  description: z.string().default(''),
  /** Who owes the fix. Free text — agencies word this very differently. */
  responsibility: z.string().default(''),
  dueDate: z.string().default(''),
})
export type Defect = z.infer<typeof defectSchema>

export const roomEntrySchema = z.object({
  id: z.string(),
  name: z.string().default(''),
  condition: roomConditionSchema.default('ok'),
  note: z.string().default(''),
  defects: z.array(defectSchema).default([]),
  photoIds: z.array(z.string()).default([]),
})
export type RoomEntry = z.infer<typeof roomEntrySchema>

export const signatureSchema = z.object({
  /** Matches the `id` of the party who signed. */
  partyId: z.string(),
  /** Photo id of the captured signature image, when signed on the device. */
  photoId: z.string().nullable().default(null),
  signedAt: z.string().nullable().default(null),
})
export type Signature = z.infer<typeof signatureSchema>

/**
 * Everything a person fills in. Stored as one JSON document rather than spread
 * over a dozen tables: it is written and read as a whole, it is frozen on
 * completion, and normalising it would buy queries nobody runs.
 */
/**
 * Things the tenant confirms, as tick boxes rather than prose.
 *
 * They are separate from `remarks` because each is a discrete legal fact that
 * gets asked about later — "was the Vermieterbescheinigung handed over?" is a
 * yes/no a year after the appointment, and finding it inside a free-text note
 * is not the same as reading it off a box.
 *
 * WHICH ones depends on the kind (`CONFIRMATIONS_BY_KIND`): what a tenant
 * confirms on the way in — certificate received, deposit paid — says nothing on
 * the way out, where the questions are about the state they leave the flat in.
 */
export const CONFIRMATION_LABELS = {
  landlordCertificate: 'Der Mieter bestätigt, die Vermieterbescheinigung erhalten zu haben',
  depositTransferred: 'Der Mieter hat die Kaution überwiesen',
  depositGuarantee: 'Der Mieter hat eine Bürgschaft „Kautionsversicherung“ übergeben',
  noDampOrMould:
    'Der Mieter versichert, dass im Mietobjekt weder Feuchtigkeit noch Schimmel vorhanden ist',
  kitchenAppliancesWork:
    'Der Mieter versichert, dass alle Elektrogeräte in der Küche funktionieren',
} as const

export type ConfirmationKey = keyof typeof CONFIRMATION_LABELS

/** In the order they are asked and printed. A sale asks none. */
export const CONFIRMATIONS_BY_KIND: Record<HandoverKind, readonly ConfirmationKey[]> = {
  rental_movein: ['landlordCertificate', 'depositTransferred', 'depositGuarantee'],
  rental_moveout: ['noDampOrMould', 'kitchenAppliancesWork'],
  sale_handover: [],
}

/**
 * The two Auszug confirmations are `boolean | null`, and the null is the point:
 * it means NEVER ASKED.
 *
 * They were added while Auszug protocols already existed, some of them signed.
 * A plain `false` default would make every one of those read "the tenant did
 * NOT assure there is no mould" — a statement nobody made, about a question
 * nobody put. So a protocol stored before they existed parses to null, a new
 * one starts at false, and `settleConfirmations` turns what is still null into
 * false at the moment a draft is completed: from then on it WAS asked.
 *
 * The three Einzug ones stay plain booleans. They have existed since the first
 * Einzug protocol, so there is no "before" for them to be null in.
 */
export const confirmationsSchema = z.object({
  landlordCertificate: z.boolean().default(false),
  depositTransferred: z.boolean().default(false),
  depositGuarantee: z.boolean().default(false),
  noDampOrMould: z.boolean().nullable().default(null),
  kitchenAppliancesWork: z.boolean().nullable().default(null),
})
export type Confirmations = z.infer<typeof confirmationsSchema>

/** What a new protocol of this kind starts with: its own questions, unticked. */
export function initialConfirmations(kind: HandoverKind): Confirmations {
  const asks = (key: ConfirmationKey) => CONFIRMATIONS_BY_KIND[kind].includes(key)

  return {
    landlordCertificate: false,
    depositTransferred: false,
    depositGuarantee: false,
    noDampOrMould: asks('noDampOrMould') ? false : null,
    kitchenAppliancesWork: asks('kitchenAppliancesWork') ? false : null,
  }
}

/**
 * Turns "never asked" into "asked, not ticked" for this kind's questions.
 *
 * Called when a draft is completed. A draft created before a question existed
 * is still shown that question in the editor, so by the time somebody signs it
 * HAS been put to them — and the stored record has to say so, or a later
 * re-render of the PDF would leave out a line the signed original carried.
 */
export function settleConfirmations(
  kind: HandoverKind,
  confirmations: Confirmations,
): Confirmations {
  const settled: Record<string, boolean | null> = { ...confirmations }
  for (const key of CONFIRMATIONS_BY_KIND[kind]) settled[key] = confirmations[key] ?? false
  return settled as Confirmations
}

/**
 * The confirmations to show or print, in order, with their state.
 *
 * ONE function for the editor and the PDF, so the two cannot disagree about
 * which boxes a protocol has. While a protocol is a draft every question of its
 * kind is live. Once completed, only the ones that were actually put — a
 * question that did not exist when it was signed must not appear on it, ticked
 * or unticked.
 */
export function shownConfirmations(
  kind: HandoverKind,
  status: HandoverStatus,
  confirmations: Confirmations,
): { key: ConfirmationKey; label: string; checked: boolean }[] {
  return CONFIRMATIONS_BY_KIND[kind].flatMap((key) => {
    const stored = confirmations[key]
    if (stored === null && status === 'completed') return []
    return [{ key, label: CONFIRMATION_LABELS[key], checked: stored ?? false }]
  })
}

export const handoverBodySchema = z.object({
  handoverAt: z.string().nullable().default(null),
  parties: z.array(partySchema).default([]),
  meters: z.array(meterReadingSchema).default([]),
  keys: z.array(keyItemSchema).default([]),
  rooms: z.array(roomEntrySchema).default([]),
  remarks: z.string().default(''),
  /** Rental only: was the deposit settled / handed over. */
  depositNote: z.string().default(''),
  /** Defaulted, so protocols written before this existed parse. */
  confirmations: confirmationsSchema.default({
    landlordCertificate: false,
    depositTransferred: false,
    depositGuarantee: false,
    noDampOrMould: null,
    kitchenAppliancesWork: null,
  }),
  signatures: z.array(signatureSchema).default([]),
})
export type HandoverBody = z.infer<typeof handoverBodySchema>

/** Address and identity of the estate, frozen when the protocol is created. */
export const estateSnapshotSchema = z.object({
  estateId: z.number().nullable(),
  externalNumber: z.string().nullable(),
  title: z.string().nullable(),
  street: z.string().nullable(),
  houseNumber: z.string().nullable(),
  postalCode: z.string().nullable(),
  city: z.string().nullable(),
  type: z.string().nullable(),
  marketingType: z.string().nullable(),
  agent: z.string().nullable(),
  /** True when the estate had to be fetched live because it was not mirrored. */
  fetchedLive: z.boolean().default(false),
})
export type EstateSnapshot = z.infer<typeof estateSnapshotSchema>

export const handoverPhotoSchema = z.object({
  id: z.string(),
  section: z.enum(['meter', 'room', 'signature', 'general']),
  refKey: z.string().nullable(),
  fileName: z.string(),
  mime: z.string(),
  size: z.number(),
  createdAt: z.string(),
})
export type HandoverPhoto = z.infer<typeof handoverPhotoSchema>

/* ------------------------------------------------------------- Fassungen --- */

/**
 * Changing a completed protocol.
 *
 * A completed protocol is a signed original, and the original is never
 * edited: every completion freezes a VERSION (Fassung) — the body as it was,
 * its own PDF, who finalised it and when. A change is made on a working copy
 * (`status: 'amending'`) and finalised into the next version, which records
 * the kind of change, the reason, and every field that differs from the
 * version before. The earlier versions and their PDFs stay retrievable.
 *
 * Two kinds, chosen by the person making the change:
 *
 *   minor  Kleine Korrektur. The signatures of the last signed version are
 *          carried over; the new PDF says so and lists what changed.
 *   major  Wesentliche Änderung. The signatures are cleared and everybody
 *          signs the new version.
 *
 * The tool does not decide which kind a change is — it WARNS. A minor change
 * that touches what the parties signed for (`BodyChange.substantive`) is
 * flagged before it is finalised and recorded in the history, never blocked:
 * the person on site knows whether a transposed digit is a typo or a dispute.
 */
export const amendmentKindSchema = z.enum(['minor', 'major'])
export type AmendmentKind = z.infer<typeof amendmentKindSchema>

export const AMENDMENT_KIND_LABELS: Record<AmendmentKind, string> = {
  minor: 'Kleine Korrektur',
  major: 'Wesentliche Änderung',
}

/** What each kind means for the signatures — the sentence under the choice. */
export const AMENDMENT_KIND_DESCRIPTIONS: Record<AmendmentKind, string> = {
  minor:
    'Ohne neue Unterschriften. Die Unterschriften der bisherigen Fassung bleiben bestehen; die Änderung wird im Protokoll ausgewiesen.',
  major:
    'Mit neuen Unterschriften. Die bisherigen Unterschriften werden entfernt und alle Beteiligten unterschreiben die neue Fassung.',
}

/** Who did something — the name is frozen, so a deleted account keeps its line. */
export const changeActorSchema = z.object({
  id: z.string().nullable(),
  name: z.string(),
})
export type ChangeActor = z.infer<typeof changeActorSchema>

/**
 * One field that differs between two versions, in words a reader of the
 * history understands. `before`/`after` are display strings; null on one side
 * means the item (a meter, a party, a defect) was added or removed.
 */
export const bodyChangeSchema = z.object({
  /** Stable key for lists: `meters.<id>.value`, `parties.<id>`, `remarks`. */
  path: z.string(),
  /** "Zähler Strom (Keller) · Zählerstand", "Mieter Max Muster · Telefon". */
  label: z.string(),
  before: z.string().nullable(),
  after: z.string().nullable(),
  /**
   * Whether this is something the parties signed for — a reading, a key
   * count, a room's condition, a defect, a confirmation, who was there, the
   * date. A telephone number or a note is not.
   */
  substantive: z.boolean(),
})
export type BodyChange = z.infer<typeof bodyChangeSchema>

/** The change under way on an `amending` protocol. */
export const pendingAmendmentSchema = z.object({
  kind: amendmentKindSchema,
  reason: z.string(),
  startedAt: z.string(),
  startedBy: changeActorSchema.nullable(),
})
export type PendingAmendment = z.infer<typeof pendingAmendmentSchema>

export const handoverVersionSchema = z.object({
  /** 1 is the original completion. */
  version: z.number().int().positive(),
  finalisedAt: z.string(),
  /** Null for a version finalised before this was recorded. */
  finalisedBy: changeActorSchema.nullable(),
  /** Null for version 1 — the original has no change to explain. */
  amendment: pendingAmendmentSchema.nullable(),
  /** Against the version before. Empty for version 1. */
  changes: z.array(bodyChangeSchema),
})
export type HandoverVersion = z.infer<typeof handoverVersionSchema>

/** A minor change that nevertheless touched something signed for. */
export function amendmentOverreaches(version: Pick<HandoverVersion, 'amendment' | 'changes'>) {
  return version.amendment?.kind === 'minor' && version.changes.some((c) => c.substantive)
}

/**
 * The version whose signatures the current document carries: the last one
 * that was signed — the original, or the last major change. A minor change
 * carries the signatures forward, so the PDF of version 3 may print
 * "Unterschriften aus Fassung 1".
 */
export function signedVersion(versions: readonly HandoverVersion[]): HandoverVersion | null {
  for (let index = versions.length - 1; index >= 0; index -= 1) {
    const version = versions[index]
    if (version && (version.amendment === null || version.amendment.kind === 'major')) {
      return version
    }
  }
  return null
}

export const startAmendmentSchema = z.object({
  kind: amendmentKindSchema,
  /** Mandatory. A change to a signed record without a stated reason is not a record. */
  reason: z.string().trim().min(3, 'Bitte eine Begründung angeben').max(500),
})
export type StartAmendment = z.infer<typeof startAmendmentSchema>

/** Switching kind or rewording the reason while the change is under way. */
export const updateAmendmentSchema = startAmendmentSchema.partial()
export type UpdateAmendment = z.infer<typeof updateAmendmentSchema>

/** `GET /handovers/:id/changes` — the working copy against the last version. */
export const bodyChangesResponseSchema = z.object({
  changes: z.array(bodyChangeSchema),
})
export type BodyChangesResponse = z.infer<typeof bodyChangesResponseSchema>

export const handoverSummarySchema = z.object({
  id: z.string(),
  kind: handoverKindSchema,
  status: handoverStatusSchema,
  estate: estateSnapshotSchema,
  handoverAt: z.string().nullable(),
  completedAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  photoCount: z.number(),
  defectCount: z.number(),
  hasPdf: z.boolean(),
  /** Number of frozen versions. 0 for a draft; 1 for a protocol completed once. */
  version: z.number().int().nonnegative(),
  /** Set while `status` is `amending`. */
  amendment: pendingAmendmentSchema.nullable(),
})
export type HandoverSummary = z.infer<typeof handoverSummarySchema>

export const handoverDetailSchema = handoverSummarySchema.extend({
  body: handoverBodySchema,
  photos: z.array(handoverPhotoSchema),
  /** Move-out protocols remember which move-in they were seeded from. */
  referenceProtocolId: z.string().nullable(),
  /** Ascending. Without the bodies — a version's PDF is fetched by number. */
  versions: z.array(handoverVersionSchema),
})
export type HandoverDetail = z.infer<typeof handoverDetailSchema>

/**
 * What the LAST completed protocol for the same object recorded — offered in
 * the editor so a reading or a key count can be taken over with one tap.
 *
 * The object number is the mapping. An Auszug and the Einzug that follows it
 * are days apart and the flat is empty in between, so the meters have barely
 * moved and the keys are the same keys: re-typing them is where the digits
 * get transposed. Nothing is stored for this — completed protocols ARE the
 * record — and nothing is written into the new protocol until somebody takes
 * it, after which it is an ordinary field like any other.
 *
 * COMPLETED protocols only, for the same reason the move-out seed takes only a
 * completed move-in: a draft holds values nobody has confirmed.
 */
export const previousMeterSchema = z.object({
  /** The row's id in the SOURCE protocol — a stable key, nothing more. */
  id: z.string(),
  kind: meterKindSchema,
  label: z.string(),
  serial: z.string(),
  value: z.string(),
  unit: z.string(),
})
export type PreviousMeter = z.infer<typeof previousMeterSchema>

export const previousKeySchema = z.object({
  id: z.string(),
  label: z.string(),
  count: z.number().int().min(0),
})
export type PreviousKey = z.infer<typeof previousKeySchema>

export const previousValuesSchema = z.object({
  /** Null when no completed protocol exists for this object yet. */
  source: z
    .object({
      id: z.string(),
      kind: handoverKindSchema,
      /** The appointment, falling back to completion — the date that gets printed. */
      recordedAt: z.string(),
    })
    .nullable(),
  meters: z.array(previousMeterSchema),
  keys: z.array(previousKeySchema),
})
export type PreviousValues = z.infer<typeof previousValuesSchema>

export const handoverListSchema = z.object({
  protocols: z.array(handoverSummarySchema),
})
export type HandoverList = z.infer<typeof handoverListSchema>

/** Estate lookup by Objekt-Nr, used to seed a new protocol. */
export const estateLookupResultSchema = z.object({
  matches: z.array(estateSnapshotSchema),
  /** Set when the mirror had nothing and onOffice was queried directly. */
  searchedLive: z.boolean(),
})
export type EstateLookupResult = z.infer<typeof estateLookupResultSchema>

export const createHandoverSchema = z.object({
  kind: handoverKindSchema,
  estateId: z.number().nullable().default(null),
  externalNumber: z.string().nullable().default(null),
  /**
   * Seed rooms, keys and meter numbers from this earlier protocol. Only ever
   * set for a move-out, pointing at the move-in for the same estate.
   */
  referenceProtocolId: z.string().nullable().default(null),
})
export type CreateHandover = z.infer<typeof createHandoverSchema>

export const updateHandoverSchema = z.object({
  body: handoverBodySchema,
})
export type UpdateHandover = z.infer<typeof updateHandoverSchema>
